// GAP-01 s1 — multi-book odds comparison engine (Wave-3 keystone).
//
// PURE, deterministic, no I/O. This is the math layer the doc's
// GAP-01 (best-price matrix), GAP-12 (arbitrage) and GAP-13
// (middling) all sit on. The fragile part — web-fetching live prices
// from the West-African book stack — is a SEPARATE later slice
// (founder ruling #1: web-search fallback). This module operates on
// whatever quotes it is handed; it never invents a price.
//
// Conventions: decimal odds. A null/absent price = suspended /
// unavailable (surfaced as such, never treated as 0 or skipped
// silently).

export interface BookQuote {
  /** Bookmaker slug, e.g. "sportybet". */
  readonly book: string;
  /** outcome key (e.g. "home"/"draw"/"away") → decimal odds, or
   *  null when that outcome is suspended at this book. */
  readonly prices: Readonly<Record<string, number | null>>;
}

export interface BestPrice {
  readonly outcome: string;
  readonly book: string | null; // null ⇒ no book has a live price
  readonly odds: number | null;
  readonly suspendedBooks: readonly string[];
}

function isLive(v: number | null | undefined): v is number {
  return typeof v === "number" && Number.isFinite(v) && v > 1;
}

/** No-vig fair probabilities for ONE book's full market. Strips the
 *  overround: p_i = (1/odds_i) / Σ(1/odds_j). Returns {} if the
 *  market is incomplete (any outcome suspended) — never guesses. */
export function noVigFairProbs(
  quote: BookQuote,
): Record<string, number> {
  const entries = Object.entries(quote.prices);
  if (entries.length === 0) return {};
  const inv: Record<string, number> = {};
  let overround = 0;
  for (const [k, v] of entries) {
    if (!isLive(v)) return {}; // incomplete book ⇒ no fair line
    inv[k] = 1 / v;
    overround += inv[k];
  }
  if (overround <= 0) return {};
  const out: Record<string, number> = {};
  for (const k of Object.keys(inv)) out[k] = inv[k]! / overround;
  return out;
}

// ── F3 (The Unabated Angle) — market hold transparency ───────────
// The bookmaker's overround on a full market = Σ(1/odds) − 1, i.e.
// exactly how much the book extracts. Pure arithmetic on whatever
// prices it is handed; it NEVER invents a price — an incomplete /
// non-live market returns null (omitted, never shown as 0%).

export type HoldClass =
  | "efficient" // tight market, < 3% — cheap to play
  | "standard" // 3%–8% — normal book margin
  | "exploitative"; // wide market, > 8% — needs a proportionally bigger edge

/** Hold (overround) for ONE market as a fraction: Σ(1/odds) − 1.
 *  Returns null if any outcome is missing/suspended or there are
 *  fewer than two outcomes — never guesses a price. */
export function marketHoldPct(
  odds: ReadonlyArray<number | null | undefined>,
): number | null {
  if (odds.length < 2) return null;
  let sum = 0;
  for (const o of odds) {
    if (!isLive(o)) return null; // incomplete market ⇒ no honest hold
    sum += 1 / o;
  }
  return sum - 1;
}

/** Classify a hold fraction. Thresholds per F3: < 3% efficient,
 *  > 8% exploitative, otherwise standard. null hold ⇒ null. */
export function classifyHold(hold: number | null): HoldClass | null {
  if (hold === null || !Number.isFinite(hold)) return null;
  if (hold < 0.03) return "efficient";
  if (hold > 0.08) return "exploitative";
  return "standard";
}

/** Best (highest) decimal price per outcome across all books. */
export function bestAcrossBooks(
  outcomes: readonly string[],
  quotes: readonly BookQuote[],
): BestPrice[] {
  return outcomes.map((outcome) => {
    let book: string | null = null;
    let odds: number | null = null;
    const suspended: string[] = [];
    for (const q of quotes) {
      const p = q.prices[outcome];
      if (!isLive(p)) {
        if (outcome in q.prices) suspended.push(q.book);
        continue;
      }
      if (odds === null || p > odds) {
        odds = p;
        book = q.book;
      }
    }
    return { outcome, book, odds, suspendedBooks: suspended };
  });
}

export interface ArbResult {
  readonly isArb: boolean;
  /** Σ 1/best_price across outcomes. < 1 ⇒ guaranteed profit. */
  readonly bookSum: number;
  /** Margin as a %, positive ⇒ profit. */
  readonly profitPct: number;
  /** Stake split (fraction of total bankroll) per outcome that
   *  equalises return. Empty when not an arb or data incomplete. */
  readonly split: ReadonlyArray<{ outcome: string; book: string; fraction: number }>;
}

/** Arbitrage across books: needs a live best price for EVERY
 *  outcome (a missing outcome ⇒ not an arb, never assumed). */
export function detectArb(best: readonly BestPrice[]): ArbResult {
  if (best.length === 0 || best.some((b) => !isLive(b.odds) || b.book === null)) {
    return { isArb: false, bookSum: NaN, profitPct: 0, split: [] };
  }
  const bookSum = best.reduce((s, b) => s + 1 / (b.odds as number), 0);
  const isArb = bookSum < 1;
  const profitPct = +(((1 / bookSum) - 1) * 100).toFixed(3);
  const split = isArb
    ? best.map((b) => ({
      outcome: b.outcome,
      book: b.book as string,
      fraction: +((1 / (b.odds as number)) / bookSum).toFixed(6),
    }))
    : [];
  return { isArb, bookSum: +bookSum.toFixed(6), profitPct, split };
}

/** Middle (GAP-13): two totals lines on different books where a
 *  result strictly between them wins BOTH. e.g. Over 2.0 @ book A
 *  and Under 3.0 @ book B → 2 or 3 goals (or whatever lands in the
 *  gap, per the line type) wins both. Returns the integer goal
 *  totals that land the middle, plus whether a true middle exists.
 *  Pure arithmetic on the lines; no probability assumption here. */
export interface MiddleResult {
  readonly hasMiddle: boolean;
  readonly overLine: number;
  readonly underLine: number;
  /** Integer totals that win both sides (empty if no middle). */
  readonly winningTotals: readonly number[];
}

export function detectTotalsMiddle(
  overLine: number,
  underLine: number,
): MiddleResult {
  const none = (): MiddleResult => ({
    hasMiddle: false,
    overLine,
    underLine,
    winningTotals: [],
  });
  if (
    !Number.isFinite(overLine) || !Number.isFinite(underLine) ||
    underLine <= overLine
  ) {
    return none(); // a middle needs Under line strictly above Over line
  }
  const totals: number[] = [];
  for (let t = Math.ceil(overLine + 1e-9); t < underLine; t += 1) {
    // Over wins if total > overLine; Under wins if total < underLine.
    if (t > overLine && t < underLine) totals.push(t);
  }
  return {
    hasMiddle: totals.length > 0,
    overLine,
    underLine,
    winningTotals: totals,
  };
}
